import React from "react";
import { Link } from "react-router-dom";
import { ChevronRight, Bot, Cloud, ShieldCheck } from "lucide-react";
import AuroraCanvas from "./AuroraCanvas";
import WaveLines from "./WaveLines";
import { useLang } from "../context/LanguageContext";

export default function Hero() {
  const { t, dir } = useLang();
  const h = t.hero;
  const flip = dir === "rtl" ? "scaleX(-1)" : "none";

  return (
    <section
      className="rq-greendark relative min-h-[92vh] flex items-center overflow-hidden"
      style={{ background: "radial-gradient(130% 100% at 70% 0%, #0f4a2b 0%, #072b1a 55%, #04140f 100%)" }}
    >
      {/* animated layers */}
      <div className="absolute inset-0 opacity-70"><AuroraCanvas /></div>
      <div className="absolute inset-0 opacity-60"><WaveLines color="63,224,127" /></div>
      <div className="absolute inset-0 rq-grid-bg opacity-[0.10]" />
      {/* big rotating glow ring */}
      <div className="absolute top-1/2 ltr:right-[-10%] rtl:left-[-10%] -translate-y-1/2 h-[720px] w-[720px] rounded-full rq-spin-slow pointer-events-none"
        style={{ background: "conic-gradient(from 0deg, transparent, rgba(63,224,127,0.18), transparent 45%)", WebkitMask: "radial-gradient(circle, transparent 58%, #000 60%)", mask: "radial-gradient(circle, transparent 58%, #000 60%)" }} />
      <div className="absolute inset-x-0 bottom-0 h-40 bg-gradient-to-t from-[#04140f] to-transparent" />

      <div className="rq-container relative z-10" style={{ paddingTop: "clamp(120px, 16vh, 168px)", paddingBottom: "clamp(48px, 8vh, 80px)" }}>
        <div className="max-w-6xl">
          <h1 className="font-display text-3xl sm:text-4xl md:text-5xl lg:text-[62px] font-bold leading-[1.08] tracking-tight text-white text-start" style={{ animation: "rq-fade-up 0.8s ease 0.1s both" }}>
            {h.title}{" "}
            <span style={{ background: "linear-gradient(120deg,#3fe07f,#c6f8d9,#3fe07f)", WebkitBackgroundClip: "text", backgroundClip: "text", WebkitTextFillColor: "transparent", backgroundSize: "200% auto", animation: "rq-gradient-shift 6s ease infinite" }}>
              {h.titleHighlight}
            </span>
          </h1>

          <p className="mt-6 max-w-3xl text-base md:text-xl leading-relaxed text-[#bfd8c9]" style={{ animation: "rq-fade-up 0.8s ease 0.2s both" }}>
            {h.subtitle}
          </p>

          <div className="mt-10 flex flex-wrap gap-4" style={{ animation: "rq-fade-up 0.8s ease 0.3s both" }}>
            <Link to="/contact" className="rq-glow-btn px-8 py-4 flex items-center gap-2 text-[15px]">
              {h.primary}
              <ChevronRight size={18} style={{ transform: flip }} />
            </Link>
            <Link to="/services" className="px-8 py-4 flex items-center gap-2 text-[15px] rounded-xl border border-[rgba(63,224,127,0.4)] text-[#7be0a8] transition-colors duration-300 hover:bg-[rgba(63,224,127,0.1)]">
              {h.secondary}
            </Link>
          </div>

          {/* floating glass feature chips */}
          <div className="mt-10 flex flex-wrap gap-4" style={{ animation: "rq-fade-up 0.8s ease 0.45s both" }}>
            {[
              { Icon: Bot, label: t.servicesList[0].title },
              { Icon: Cloud, label: t.servicesList[4].title },
              { Icon: ShieldCheck, label: t.servicesList[10].title },
            ].map(({ Icon, label }, i) => (
              <div key={i} className="rq-float flex items-center gap-2.5 rounded-xl border border-[rgba(255,255,255,0.14)] bg-[rgba(255,255,255,0.06)] px-4 py-3 backdrop-blur-md" style={{ animationDelay: `${i * 0.5}s` }}>
                <Icon size={18} className="text-[#3fe07f]" />
                <span className="text-sm font-semibold text-white">{label}</span>
              </div>
            ))}
          </div>

          {/* stats strip */}
          <div className="mt-10 grid grid-cols-3 gap-6 max-w-2xl border-t border-white/10 pt-8" style={{ animation: "rq-fade-up 0.8s ease 0.6s both" }}>
            {t.stats.map((s, i) => (
              <div key={i}>
                <div className="font-display text-2xl md:text-4xl font-bold text-[#3fe07f]">{s.value}{s.suffix}</div>
                <div className="mt-1 text-xs md:text-sm text-[#a9c9b6]">{s.label}</div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* scroll cue */}
      <div className="absolute bottom-6 left-1/2 -translate-x-1/2 flex flex-col items-center gap-2 z-10">
        <span className="h-10 w-6 rounded-full border border-[rgba(63,224,127,0.5)] flex justify-center pt-2">
          <span className="h-2 w-1 rounded-full bg-[#3fe07f] animate-bounce" />
        </span>
      </div>
    </section>
  );
}
